import React, { useMemo, useState, useEffect } from 'react'
import { useFormikContext, useField } from 'formik'
import { debounce, throttle } from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import { useNewTranslation } from 'hooks'
import { BrandState, brandListRequest, clearBrand, getBrand } from 'state/brands'
import { STATE_KEYS, setPagination } from 'state/requests'
import { createGetRequestsAreLoading, getBrandsList, createGetRequestPagination } from 'state'
import { selectIsInternalUser } from 'state/profile'
import { selectCountryFilter } from 'state/filter'
import Dropdown, { DropdownProps } from 'particles/Dropdown'
import MenuItem from 'particles/MenuItem'
import { EmptySearchResult } from 'particles/MenuItem/Search'
import { TemplateFilterSchema } from 'components/Campaign/Template/TemplateSelector'
import { FormValues } from 'components/Campaign/Editor/Register/Register'
import BrandCard from './BrandCard'
import { useSelectedBrand } from './subscriber'

interface OptionState {
    label: string
    value: string
    secondInfo?: string
}

interface BrandDropdownProps extends DropdownProps {
    isReadOnly?: boolean
    campaignBrand?: BrandState
    isAbleRestored?: boolean
    isSetInitSearch?: boolean
    identifier?: string
    onBrandUpdateSuccess?: () => void
}

const BrandDropdown: React.FC<BrandDropdownProps> = ({
    isReadOnly = false,
    onClose,
    campaignBrand,
    isAbleRestored = false,
    isSetInitSearch = true,
    identifier,
    onBrandUpdateSuccess,
}) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const { values, setFieldValue, setFieldError } = useFormikContext<TemplateFilterSchema | FormValues>()
    const [, meta] = useField('brandUuid')
    const [search, setSearch] = useState<string>('')
    const [isInitialSearch, setIsinitialSearch] = useState<boolean>(true)
    const [selectedBrandKeyword, setSelectedBrandKeyword] = useState<string>('')
    const [selectedBrand, setSelectedBrand] = useSelectedBrand()
    // editingCampaignCountry is used to provide faster country retrieval for number input budgetCurrencySign
    // because, waiting brandlist is took some time
    const isInternalUser = useSelector(selectIsInternalUser)
    const brandsList = useSelector(getBrandsList)
    const pagination = useSelector(createGetRequestPagination(STATE_KEYS.BrandsList))
    const selectedBrandUuid = values.brandUuid
    const isLoading = useSelector(createGetRequestsAreLoading([STATE_KEYS.BrandsList]))
    const countryFilter = useSelector(selectCountryFilter)

    const fetchBrands = (keyword: string, countryFilter: string) => {
        dispatch(
            brandListRequest({
                country: countryFilter,
                keyword: keyword,
            }),
        )
    }
    const brandOptions = useMemo(() => {
        return brandsList.map((brand: BrandState) => {
            return {
                value: brand.uuid,
                label: `${brand.name} (${brand.country})`,
                secondInfo: isInternalUser && brand.team_name ? brand.team_name : undefined,
            }
        })
    }, [brandsList, isLoading])

    const handleChange = (value?: string) => {
        setFieldError('brandUuid', '')
        setFieldError('brand', '')
        setFieldValue('brandUuid', value)
        // Ensure that after the user selects a brand and the brand list updates with only the selected brand,
        // they can still restore the default brand list by setting the selected brand name in the search.
        const localSelectedBrand = brandsList.find((brand: BrandState) => brand.uuid === value)
        if (localSelectedBrand?.name && isSetInitSearch) {
            setSearch(localSelectedBrand?.name)
            if (localSelectedBrand?.name != selectedBrandKeyword) {
                setSelectedBrandKeyword(localSelectedBrand?.name)
            }
        }
    }

    const handleFetchMoreData = throttle(() => {
        const { next } = pagination
        if (next && !isLoading) {
            fetchBrands(search, countryFilter)
        }
    }, 200)

    const onClearBrand = () => {
        dispatch(clearBrand())
        dispatch(
            setPagination({
                stateKey: STATE_KEYS.BrandsList,
                count: null,
                next: '',
                previous: pagination?.previous || '',
            }),
        )
    }

    const handleSearch = debounce((value: string) => {
        onClearBrand()
        setSearch(value)
        fetchBrands(value, countryFilter)
    }, 1000)

    const handleClearSearch = () => {
        handleSearch('')
    }

    const handleRestoreSearch = (event?: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
        if (reason && selectedBrandKeyword != search) {
            handleSearch(selectedBrandKeyword ? selectedBrandKeyword : '')
        }
    }

    useEffect(() => {
        if (selectedBrandUuid && isSetInitSearch) {
            // if user has selected some brand before. fetch brand detail to make brandList must have that brand item
            dispatch(getBrand({ uuid: selectedBrandUuid }))
        } else {
            dispatch(brandListRequest({ country: countryFilter }))
        }

        return () => {
            dispatch(clearBrand())
            setSelectedBrand(null)
        }
    }, [dispatch, countryFilter, selectedBrandUuid, isSetInitSearch])

    useEffect(() => {
        const localSelectedBrand =
            (selectedBrandUuid && brandsList.find((brand: BrandState) => brand.uuid === selectedBrandUuid)) ||
            campaignBrand
        if (localSelectedBrand && localSelectedBrand?.uuid === selectedBrandUuid) {
            setSelectedBrand(localSelectedBrand)
            // Assign brand name to search and selectedBrandKeyword to ensure the selected brand is displayed.
            // This avoids issues where a user has selected a brand with a keyword-filtered result,
            // as the default brand list may not include the selected brand item.
            if (isInitialSearch && isSetInitSearch) {
                setSearch(localSelectedBrand.name)
                setSelectedBrandKeyword(localSelectedBrand.name)
                setIsinitialSearch(false)
            }
        }
    }, [brandsList, selectedBrandUuid, campaignBrand, isSetInitSearch])

    return (
        <>
            <Dropdown
                errorMessage={meta.touched && meta.error ? meta.error : undefined}
                label={t('campaign.template.brandName.title')}
                subtext={t('campaign.template.brandName.subText')}
                isRequired
                onChange={value => handleChange(value as string)}
                value={selectedBrand?.uuid}
                renderValue={() => {
                    if (selectedBrand) {
                        const teamName = selectedBrand?.team_name || selectedBrand?.team?.name
                        return `${selectedBrand?.name}${selectedBrand?.country ? ' (' + selectedBrand?.country + ')' : ''}${isInternalUser && teamName ? ' | ' + teamName : ''}`
                    } else {
                        return t('placeholderText.enterBrandName')
                    }
                }}
                onClose={(event?: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
                    if (onClose) {
                        onClose()
                    }
                    if (isAbleRestored) {
                        handleRestoreSearch(event, reason)
                    }
                }}
                placeholder={t('placeholderText.enterBrandName')}
                onScrollBottomCallback={handleFetchMoreData}
                isReadOnly={isReadOnly}
                labelSize="l"
                isLoading={isLoading}
                maxWidth={'100%'}
                identifier={identifier}
                dataTestId="test-campaign-brand-dropdown"
            >
                <MenuItem.Search onSearch={handleSearch} onClearSearch={handleClearSearch} searchValue={search} />
                {brandOptions.length > 0 ? (
                    brandOptions.map((item: OptionState, index: number) => (
                        <MenuItem.Text
                            key={`menu-item-${index}`}
                            tabIndex={index}
                            text={item.secondInfo ? item.label + ' | ' + item.secondInfo : item.label}
                            value={item.value}
                        />
                    ))
                ) : (
                    <EmptySearchResult />
                )}
            </Dropdown>
            <BrandCard selectedBrand={selectedBrand} onBrandUpdateSuccess={onBrandUpdateSuccess} />
        </>
    )
}

export default BrandDropdown
